import { Pencil, Settings2, Trash2, Wrench, Zap } from 'lucide-react-native';
import { useCallback } from 'react';
import { StyleSheet } from 'react-native';
import { Badge, Box, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { type OrgAgentSkill, SkillExecutionMode } from '../types';

interface SkillToggleCardProps {
  skill: OrgAgentSkill;
  onToggle: (skillId: string, enabled: boolean) => void;
  isToggling: boolean;
  onEdit?: (skill: OrgAgentSkill) => void;
  onDelete?: (skillId: string) => void;
  testID?: string;
}

export function SkillToggleCard({
  skill,
  onToggle,
  isToggling,
  onEdit,
  onDelete,
  testID,
}: Readonly<SkillToggleCardProps>) {
  const { t } = useTranslation();

  const handlePress = useCallback(() => {
    if (!isToggling && !skill.isCustom) {
      onToggle(skill.id, !skill.enabled);
    }
  }, [skill.id, skill.enabled, skill.isCustom, onToggle, isToggling]);

  return (
    <Box
      testID={testID}
      pressable={!skill.isCustom}
      onPress={handlePress}
      borderRadius="md"
      padding="sm"
      backgroundColor={skill.enabled ? colors.primary.muted : 'transparent'}
      style={[styles.card, skill.enabled && styles.cardEnabled]}
    >
      <HStack spacing="sm" align="center" justify="between">
        <HStack spacing="sm" align="center" style={styles.content}>
          <Box
            backgroundColor={skill.enabled ? colors.primary.subtle : colors.background.tertiary}
            borderRadius="full"
            alignItems="center"
            justifyContent="center"
            style={styles.iconBox}
          >
            <Zap
              size={12}
              color={skill.enabled ? colors.primary[400] : colors.text.tertiary}
              strokeWidth={2}
            />
          </Box>
          <VStack spacing="xxs" style={styles.textContent}>
            <HStack spacing="xs" align="center">
              <Text
                variant="caption"
                color={skill.enabled ? colors.text.primary : colors.text.tertiary}
                style={styles.skillName}
                numberOfLines={1}
              >
                {skill.name}
              </Text>
              {skill.isCustom && (
                <Badge color="blue" variant="subtle" size="sm">
                  {t('agents.skills.custom')}
                </Badge>
              )}
            </HStack>
            <Text
              variant="caption"
              color={colors.text.tertiary}
              numberOfLines={2}
              style={styles.description}
            >
              {skill.description}
            </Text>
            {(skill.toolNames?.length > 0 ||
              skill.executionMode !== SkillExecutionMode.PROMPT_ONLY) && (
              <HStack spacing="xs" align="center" style={styles.metaBadges}>
                {skill.executionMode === SkillExecutionMode.AUTO_EXECUTE && (
                  <HStack spacing="xxs" align="center">
                    <Zap size={9} color={colors.status.success.main} strokeWidth={2.5} />
                    <Text
                      variant="caption"
                      color={colors.status.success.main}
                      style={styles.metaText}
                    >
                      {t('agents.skills.autoExec')}
                    </Text>
                  </HStack>
                )}
                {skill.executionMode === SkillExecutionMode.REQUIRE_APPROVAL && (
                  <HStack spacing="xxs" align="center">
                    <Settings2 size={9} color={colors.status.warning.main} strokeWidth={2.5} />
                    <Text
                      variant="caption"
                      color={colors.status.warning.main}
                      style={styles.metaText}
                    >
                      {t('agents.skills.approval')}
                    </Text>
                  </HStack>
                )}
                {skill.toolNames?.length > 0 && (
                  <HStack spacing="xxs" align="center">
                    <Wrench size={9} color={colors.text.tertiary} strokeWidth={2.5} />
                    <Text variant="caption" color={colors.text.tertiary} style={styles.metaText}>
                      {skill.toolNames.length}
                    </Text>
                  </HStack>
                )}
              </HStack>
            )}
          </VStack>
        </HStack>
        {skill.isCustom ? (
          <HStack spacing="xs" align="center">
            {onEdit && (
              <Box
                pressable
                onPress={() => onEdit(skill)}
                borderRadius="full"
                alignItems="center"
                justifyContent="center"
                style={styles.actionButton}
              >
                <Pencil size={12} color={colors.text.tertiary} strokeWidth={2} />
              </Box>
            )}
            {onDelete && (
              <Box
                pressable
                onPress={() => onDelete(skill.id)}
                borderRadius="full"
                alignItems="center"
                justifyContent="center"
                style={styles.actionButton}
              >
                <Trash2 size={12} color={colors.status.error.main} strokeWidth={2} />
              </Box>
            )}
          </HStack>
        ) : (
          <Badge color={skill.enabled ? 'green' : 'neutral'} variant="subtle" size="sm">
            {skill.enabled ? t('common.states.on') : t('common.states.off')}
          </Badge>
        )}
      </HStack>
    </Box>
  );
}

const styles = StyleSheet.create({
  card: {
    minHeight: 56,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  cardEnabled: {
    borderColor: colors.primary.subtle,
  },
  content: {
    flex: 1,
  },
  iconBox: {
    width: 28,
    height: 28,
  },
  textContent: {
    flex: 1,
  },
  skillName: {
    fontWeight: '600',
  },
  description: {
    fontSize: 11,
    lineHeight: 14,
  },
  actionButton: {
    width: 26,
    height: 26,
    backgroundColor: colors.background.tertiary,
  },
  metaBadges: {
    marginTop: 2,
  },
  metaText: {
    fontSize: 9,
    fontWeight: '600',
  },
});
